import { useState, useRef, useEffect } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'
import { formatCurrency } from '@utils/format.js'
import { addDocumento, getDocumento, updateDocumento } from '@services/crud.js'
import { formatBytes, getFileIcon } from '@components/documentos/utils.js'
import type { Documento, OrcamentoMaterial } from '@services/db.js'

type Props = {
  open: boolean
  items: OrcamentoMaterial[]
  onClose: () => void
  onEscolher: (grupoId: string, escolhidoId: number) => Promise<void>
  onUpdate: (id: number, patch: Partial<OrcamentoMaterial>) => Promise<void>
}

export function EscolherOrcamentoModal({ open, items, onClose, onEscolher, onUpdate }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(items[0]?.id ?? null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [existingDoc, setExistingDoc] = useState<Documento | null>(null)
  const [loadingDoc, setLoadingDoc] = useState(false)
  const [saving, setSaving] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const nfOnly =
    items.length === 1 && (items[0]?.status === 'escolhido' || items[0]?.status === 'comprado')
  const grupoId = items[0]?.grupoId ?? ''
  const material = items[0]?.material ?? 'Material'
  const hasDisplayFile = Boolean(arquivo || existingDoc)

  useEffect(() => {
    if (!open) {
      setArquivo(null)
      setExistingDoc(null)
      setSelectedId(items[0]?.id ?? null)
      return
    }
    setSelectedId(items[0]?.id ?? null)
    setArquivo(null)

    const isNfOnly =
      items.length === 1 && (items[0]?.status === 'escolhido' || items[0]?.status === 'comprado')
    const docId = isNfOnly ? items[0]?.documentoId : undefined

    if (!docId) {
      setExistingDoc(null)
      return
    }

    setLoadingDoc(true)
    void getDocumento(docId)
      .then((doc) => setExistingDoc(doc ?? null))
      .finally(() => setLoadingDoc(false))
  }, [open, items])

  async function handleConfirm() {
    if (!selectedId) return
    setSaving(true)
    try {
      if (!nfOnly) {
        await onEscolher(grupoId, selectedId)
      }
      const item = items.find((o) => o.id === selectedId) ?? items[0]
      if (arquivo && item?.id) {
        if (item.documentoId) {
          await updateDocumento(
            item.documentoId,
            {
              nome: `NF · ${item.material} · ${item.fornecedor}`,
              tipo: arquivo.type || 'application/octet-stream',
              tamanho: arquivo.size,
              fornecedor: item.fornecedor,
              valor: item.valorTotal,
            },
            arquivo,
          )
          await onUpdate(item.id, { status: 'comprado' })
        } else {
          const docId = await addDocumento(
            {
              nome: `NF · ${item.material} · ${item.fornecedor}`,
              categoria: 'Notas Fiscais',
              tipo: arquivo.type || 'application/octet-stream',
              tamanho: arquivo.size,
              fornecedor: item.fornecedor,
              valor: item.valorTotal,
              data: new Date().toISOString().slice(0, 10),
              observacoes: `Orçamento de material: ${item.material}`,
            },
            arquivo,
          )
          await onUpdate(item.id, { documentoId: docId, status: 'comprado' })
        }
      }
      onClose()
    } finally {
      setSaving(false)
    }
  }

  function handlePickFile() {
    fileRef.current?.click()
  }

  const displayName = arquivo?.name ?? existingDoc?.nome ?? ''
  const displayTipo = arquivo?.type ?? existingDoc?.tipo ?? ''
  const displaySize = arquivo?.size ?? existingDoc?.tamanho ?? 0

  return (
    <Modal
      open={open}
      title={nfOnly ? 'Nota fiscal' : 'Definir fornecedor'}
      size='md'
      onClose={onClose}
      actions={
        <>
          <Button variant='ghost' size='sm' onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            size='sm'
            onClick={handleConfirm}
            loading={saving}
            disabled={!selectedId || loadingDoc}
          >
            {nfOnly ? (arquivo ? 'Salvar NF' : 'Fechar') : 'Confirmar escolha'}
          </Button>
        </>
      }
    >
      {!nfOnly && (
        <>
          <p className='cf3-mat-escolher__intro'>
            Escolha o fornecedor vencedor para <strong>{material}</strong>. O valor entra no
            Financeiro como previsto. Só conta no saldo quando você pagar ou anexar a NF.
          </p>
          <ul className='cf3-mat-compare'>
            {items.map((o) => (
              <li key={o.id}>
                <label className='cf3-mat-compare__item'>
                  <input
                    type='radio'
                    name='orcamento'
                    checked={selectedId === o.id}
                    onChange={() => setSelectedId(o.id ?? null)}
                  />
                  <span className='cf3-mat-compare__fornecedor'>{o.fornecedor}</span>
                  <span className='cf3-mat-compare__valor'>{formatCurrency(o.valorTotal)}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className='cf3-mat-nf-upload'>
        <p className='cf3-mat-nf-upload__label'>
          {nfOnly ? 'Nota fiscal anexada' : 'Nota fiscal (opcional)'}
        </p>
        <div className='cf3-doc-file-area cf3-mat-nf-upload__area'>
          {loadingDoc ? (
            <p className='cf3-mat-nf-upload__loading'>Carregando arquivo…</p>
          ) : hasDisplayFile ? (
            <div className='cf3-doc-file-area__info'>
              <span className='cf3-doc-file-area__icon' aria-hidden>
                {getFileIcon(displayTipo)}
              </span>
              <span className='cf3-doc-file-area__filename'>{displayName}</span>
              {displaySize > 0 && (
                <span className='cf3-doc-file-area__size'>{formatBytes(displaySize)}</span>
              )}
              {existingDoc && !arquivo && (
                <span className='cf3-mat-nf-upload__current'>Arquivo atual nos Arquivos</span>
              )}
            </div>
          ) : (
            <div className='cf3-doc-file-area__placeholder'>
              <span className='cf3-doc-file-area__placeholder-label' aria-hidden>
                NF
              </span>
              <p>Nenhum arquivo anexado</p>
            </div>
          )}
          <Button variant='secondary' size='sm' type='button' onClick={handlePickFile}>
            {hasDisplayFile ? 'Trocar arquivo' : 'Escolher arquivo'}
          </Button>
          <input
            ref={fileRef}
            type='file'
            accept='image/*,.pdf'
            className='cf3-doc-file-area__input'
            tabIndex={-1}
            aria-hidden
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
        </div>
        <p className='cf3-mat-nf-upload__hint'>O arquivo fica salvo em Arquivos da obra.</p>
      </div>
    </Modal>
  )
}
